import React, { useEffect, useRef, useState } from 'react';
import { Terminal, Shield, Eye, Code } from 'lucide-react';
import anime from 'animejs';
import animationManager from '../utils/AnimationManager';

/**
 * ProjectCards - 3D flip cards showcasing projects
 */
const ProjectCards = () => {
  const cardsRef = useRef(null);
  const [flipped, setFlipped] = useState({});

  const projects = [
    {
      id: 1,
      title: 'Wireless Pen Testing',
      icon: Shield,
      description: 'Advanced wireless network penetration testing using Airgeddon framework',
      tags: ['WiFi', 'Airgeddon', 'Kali Linux'],
      color: '#00ff41'
    },
    {
      id: 2,
      title: 'OSINT Investigations',
      icon: Eye,
      description: 'Deep dive OSINT investigations using publicly available information',
      tags: ['OSINT', 'Reconnaissance', 'Intelligence'],
      color: '#9d4edd'
    },
    {
      id: 3,
      title: 'Exploit Development',
      icon: Code,
      description: 'Custom exploit development and automation with Bash scripting',
      tags: ['Bash', 'Exploits', 'Automation'],
      color: '#00d9ff'
    }
  ];

  useEffect(() => {
    if (!cardsRef.current || !animationManager.shouldAnimate()) return;

    const cards = cardsRef.current.querySelectorAll('.project-card');
    
    anime({
      targets: cards,
      opacity: [0, 1],
      translateY: [50, 0],
      rotateX: [10, 0],
      delay: anime.stagger(200),
      duration: 800,
      easing: 'easeOutCubic'
    });
  }, []);

  const handleCardClick = (id) => {
    if (!animationManager.shouldAnimate()) return;

    setFlipped(prev => ({ ...prev, [id]: !prev[id] }));
    
    const card = cardsRef.current?.querySelector(`[data-card-id="${id}"]`);
    if (card) {
      anime({
        targets: card,
        rotateY: flipped[id] ? 0 : 180,
        duration: 600,
        easing: 'easeInOutCubic'
      });
    }
  };

  const handleCardHover = (e) => {
    if (!animationManager.shouldAnimate()) return;
    
    // Skip 3D tilt on touch devices for better mobile performance
    if (window.innerWidth < 768 || 'ontouchstart' in window) return;

    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const rotateX = (y - centerY) / 10;
    const rotateY = (centerX - x) / 10;

    anime({
      targets: card,
      rotateX: -rotateX,
      rotateY: rotateY,
      duration: 300,
      easing: 'easeOutCubic'
    });
  };

  const handleCardLeave = (e) => {
    if (!animationManager.shouldAnimate()) return;

    anime({
      targets: e.currentTarget,
      rotateX: 0,
      rotateY: flipped[e.currentTarget.dataset.cardId] ? 180 : 0,
      duration: 500,
      easing: 'easeOutElastic(1, .6)'
    });
  };

  return (
    <div
      ref={cardsRef}
      className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6 my-4 sm:my-6 md:my-8"
      style={{ fontFamily: "'Rajdhani', sans-serif" }}
    >
      {projects.map((project) => {
        const Icon = project.icon;
        return (
          <div
            key={project.id}
            data-card-id={project.id}
            className="project-card relative h-56 sm:h-60 md:h-64 cursor-pointer perspective-1000"
            style={{ transformStyle: 'preserve-3d' }}
            onClick={() => handleCardClick(project.id)}
            onMouseMove={handleCardHover}
            onMouseLeave={handleCardLeave}
          >
            <div className="relative w-full h-full bg-gradient-to-br from-[#141432] to-[#0e0e22] rounded-lg sm:rounded-xl border sm:border-2 border-opacity-30 p-3 sm:p-4 md:p-6 overflow-hidden"
                 style={{ 
                   borderColor: project.color,
                   backfaceVisibility: 'hidden'
                 }}>              {/* Front Face */}
              <div className="flex flex-col h-full">
                <div className="flex items-center justify-between mb-2 sm:mb-3 md:mb-4">
                  <Icon className="w-7 h-7 sm:w-8 sm:h-8 md:w-10 md:h-10" style={{ color: project.color }} />
                  <Terminal className="w-4 h-4 sm:w-5 sm:h-5 text-[#00ff41]/50" />
                </div>
                
                <h3 className="text-base sm:text-lg md:text-xl font-bold mb-2 sm:mb-3" style={{ color: project.color }}>
                  {project.title}
                </h3>
                
                <p className="text-xs sm:text-sm text-gray-300 mb-3 sm:mb-4 flex-grow">
                  {project.description}
                </p>
                
                <div className="flex flex-wrap gap-1.5 sm:gap-2">
                  {project.tags.map((tag, idx) => (
                    <span
                      key={idx}
                      className="px-2 sm:px-3 py-0.5 sm:py-1 text-[10px] sm:text-xs rounded-full border"
                      style={{
                        borderColor: `${project.color}40`,
                        backgroundColor: `${project.color}10`,
                        color: project.color
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Ambient glow */}
              <div
                className="absolute inset-0 opacity-20 blur-2xl pointer-events-none"
                style={{ backgroundColor: project.color }}
              />

              {/* Click indicator */}
              <div className="absolute bottom-1.5 sm:bottom-2 right-1.5 sm:right-2 text-[10px] sm:text-xs opacity-50" style={{ color: project.color }}>
                <span className="hidden sm:inline">Click to flip →</span>
                <span className="sm:hidden">→</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ProjectCards;
